/* eslint-disable react/prop-types */
import { FC, useState } from 'react';
import { ListingNew } from '../../model/Listing';
import { Typo } from '../../components/Typo';
import { FormattedMessage, useIntl } from 'react-intl';
import { TRANSLATED_CONSTANTS } from '../../static/translatedConstants';
import StorefrontGridCard from '../../components/shared/ListingCard/StorefrontGridCard';
import { Storefront } from '../../model/Storefront';
import Button from '../../components/shared/Button';
import { AuthUserState } from '../../context/AuthUserProvider';
import { useGetStorefrontItems } from '../../hooks/storfront/useGetStorefronts';

// Show-all with a safety cap: render up to this many at once (images lazy-load),
// and only show a "Load more" if a shop somehow exceeds it.
const PAGE = 60;

type SortKey = 'newest' | 'price_asc' | 'price_desc';

type Props = {
  storefront: Storefront;
  onSave?: (id: number | string, type: string, like: boolean, savedListingId?: number) => void;
  // Hide the current item from its own page's "more from this shop".
  excludeId?: number | string;
  // Show the search + sort tools (on the storefront page, not the item-page embed).
  showTools?: boolean;
};

// A storefront item can't be bought when it's sold or out of stock. We still SHOW
// it (the card marks it), but sort it after the buyable ones.
const isUnavailable = (l: ListingNew): boolean =>
  !!l?.isSold ||
  (l as any)?.available === false ||
  (!l?.unlimitedStock && l?.quantity != null && Number(l?.quantity) <= 0);

const priceOf = (l: ListingNew): number => Number(l?.price?.priceInUSD) || 0;
const createdOf = (l: ListingNew): number => new Date((l as any)?.createdAt || 0).getTime();

const StorefrontItems: FC<Props> = ({ storefront, onSave, excludeId, showTools }) => {
  const { me } = AuthUserState();
  const intl = useIntl();
  const [limit, setLimit] = useState(PAGE);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('newest');
  const { listings, total } = useGetStorefrontItems(me?.id, Number(storefront?.id), limit, 0);

  // W-D (three-intent): only 'dogelist' shops check out here. For website /
  // in-person shops we keep the catalog but reframe it so it doesn't read as a
  // DogeList "buy" surface. Unknown/legacy mode falls back to the sale wording.
  const nonDogelist = !!storefront?.sellingMode && storefront.sellingMode !== 'dogelist';
  const isWebsite = storefront?.sellingMode === 'website';

  const fetched: ListingNew[] = Array.isArray(listings) ? [...listings] : [];
  const q = search.trim().toLowerCase();
  const items = fetched
    .filter((l) => (excludeId == null ? true : String(l?.id) !== String(excludeId)))
    .filter((l) => (q ? (l?.name || '').toLowerCase().includes(q) : true));

  // Available items always first; the chosen sort orders within each group.
  items.sort((a, b) => {
    const ua = Number(isUnavailable(a));
    const ub = Number(isUnavailable(b));
    if (ua !== ub) {
      return ua - ub;
    }
    if (sort === 'price_asc') {
      return priceOf(a) - priceOf(b);
    }
    if (sort === 'price_desc') {
      return priceOf(b) - priceOf(a);
    }
    return createdOf(b) - createdOf(a);
  });

  const shown = items.length;
  const loadedCount = fetched.length;
  const serverTotal = Number(total) || loadedCount;
  const canLoadMore = serverTotal > loadedCount;

  return (
    <div className="w-full">
      <Typo.headingXs fontWeight="font-medium" className="text-gray-900 mb-1">
        {nonDogelist ? (
          <FormattedMessage id="page.storefront.items_heading" defaultMessage="Items" />
        ) : (
          <FormattedMessage
            id={TRANSLATED_CONSTANTS.ITEMS_FOR_SALE.id}
            defaultMessage={TRANSLATED_CONSTANTS.ITEMS_FOR_SALE.defaultMessage}
          />
        )}
      </Typo.headingXs>
      <Typo.textMd fontWeight="font-normal" className="text-gray-500">
        {nonDogelist ? (
          isWebsite ? (
            <FormattedMessage
              id="page.storefront.items_sub_website"
              defaultMessage="Browse what this shop offers, then buy on their website."
            />
          ) : (
            <FormattedMessage
              id="page.storefront.items_sub_inperson"
              defaultMessage="Browse what this shop offers, then visit in person to buy."
            />
          )
        ) : (
          <FormattedMessage
            id={TRANSLATED_CONSTANTS.VIEW_WHAT_STORE_HAS_FOR_SALE.id}
            defaultMessage={TRANSLATED_CONSTANTS.VIEW_WHAT_STORE_HAS_FOR_SALE.defaultMessage}
          />
        )}
      </Typo.textMd>

      {showTools ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'page.storefront.search_shop',
              defaultMessage: 'Search this shop'
            })}
            className="w-full max-w-[280px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-yellow-500 focus:outline-none"
          />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-yellow-500 focus:outline-none">
            <option value="newest">
              {intl.formatMessage({ id: 'page.storefront.sort_newest', defaultMessage: 'Newest' })}
            </option>
            <option value="price_asc">
              {intl.formatMessage({
                id: 'page.storefront.sort_price_asc',
                defaultMessage: 'Price: low to high'
              })}
            </option>
            <option value="price_desc">
              {intl.formatMessage({
                id: 'page.storefront.sort_price_desc',
                defaultMessage: 'Price: high to low'
              })}
            </option>
          </select>
        </div>
      ) : null}

      {shown === 0 ? (
        <div className="mt-6 rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-500">
          {q ? (
            <FormattedMessage
              id="page.storefront.items_empty_search"
              defaultMessage="No items match your search."
            />
          ) : nonDogelist ? (
            <FormattedMessage
              id="page.storefront.items_empty_listed"
              defaultMessage="This shop has no items listed yet."
            />
          ) : (
            <FormattedMessage
              id="page.storefront.items_empty_sale"
              defaultMessage="This shop has no items for sale yet."
            />
          )}
        </div>
      ) : (
        <div className="py-6 grid grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {items.map((listing, index) => (
            <StorefrontGridCard key={listing?.id ?? index} listing={listing} onSave={onSave} />
          ))}
        </div>
      )}

      {canLoadMore ? (
        <div className="w-full flex justify-center">
          <Button onClick={() => setLimit(limit + PAGE)} size="lg" theme="base">
            <Typo.textMd fontWeight="font-semibold">
              <FormattedMessage id="shared.load_more" defaultMessage="Load more" />
            </Typo.textMd>
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default StorefrontItems;
